import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { BRANDS } from "@/lib/brands";

/**
 * Site footer — corporate, UK-led, with Kosovo presence.
 * No phone numbers, no personal email. Single contact: info@erenown.com.
 * Legal links are titles only (Privacy, Terms, Cookie, Legal Notice).
 */
const NAV = [
  { to: "/", label: "Arrival", key: "arrival" },
  { to: "/brand", label: "The Brand", key: "brand" },
  { to: "/orbit", label: "Orbit", key: "orbit" },
  { to: "/capabilities", label: "Capabilities", key: "capabilities" },
  { to: "/contact", label: "Contact", key: "contact" },
];

const LEGAL = [
  { key: "privacy",     label: "Privacy Policy" },
  { key: "terms",       label: "Terms of Use" },
  { key: "cookie",      label: "Cookie Policy" },
  { key: "legalnotice", label: "Legal Notice" },
];

function ColTitle({ children }) {
  return (
    <div
      className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-5"
      style={{ color: T.accent }}
    >
      {children}
    </div>
  );
}

export default function SiteFooter() {
  return (
    <footer
      data-testid="site-footer"
      className="relative"
      style={{ background: T.bg, borderTop: `1px solid ${T.border}`, color: T.text }}
    >
      {/* Brand-line strip — names only, no slogans */}
      <div className="border-b" style={{ borderColor: T.border }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-12">
          <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.28em] uppercase mb-8" style={{ color: T.muted }}>
            <span>Brand Lines</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>05 · Aligned</span>
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-10">
            {BRANDS.map((b, i) => (
              <li key={b.id} data-testid={`footer-brandstrip-${b.id}`}>
                <Link to={`/orbit?brand=${b.id}`} className="group block">
                  <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-2 tabular-nums" style={{ color: T.muted }}>
                    0{i + 1}
                  </div>
                  <div className="font-fraunces text-2xl md:text-[26px] leading-none" style={{ color: T.text, fontVariationSettings: "'SOFT' 40" }}>
                    {b.name}
                  </div>
                  <div className="mt-3 h-px w-6 group-hover:w-20 transition-all duration-500" style={{ background: T.accent }} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main grid — 4 corporate columns (Brand Lines column removed; brand strip above already lists them) */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-12 gap-10 lg:gap-12">
        {/* Column 1 — Erenown */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Link
            to="/"
            data-testid="footer-wordmark"
            className="inline-flex items-baseline gap-2 font-bricolage font-semibold uppercase"
            style={{ color: T.text, letterSpacing: "0.22em", fontSize: 14 }}
          >
            <span style={{ color: T.warm }}>E</span>
            <span>renown</span>
          </Link>
          <p
            className="mt-6 max-w-xs font-intertight text-[14px] leading-[1.85]"
            style={{ color: T.mutedSoft }}
          >
            The central structure behind a disciplined orbit of brands, systems, and cross-border solution
            lines.
          </p>
        </div>

        {/* Column 2 — Navigation */}
        <div className="col-span-6 md:col-span-6 lg:col-span-2">
          <ColTitle>Navigation</ColTitle>
          <ul className="space-y-3 font-plexmono text-[11px] tracking-[0.22em] uppercase" style={{ color: T.mutedSoft }}>
            {NAV.map((l) => (
              <li key={l.key}>
                <Link
                  data-testid={`footer-link-${l.key}`}
                  to={l.to}
                  className="transition-colors hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3 — Contact */}
        <div className="col-span-6 md:col-span-6 lg:col-span-3">
          <ColTitle>Contact</ColTitle>
          <a
            href="mailto:info@erenown.com"
            data-testid="footer-email"
            className="font-fraunces text-lg md:text-xl border-b inline-block"
            style={{ color: T.text, borderColor: T.accent }}
          >
            info@erenown.com
          </a>
          <p
            className="mt-5 font-intertight text-[12.5px] leading-[1.85] max-w-[15rem]"
            style={{ color: T.mutedSoft }}
          >
            All enquiries are reviewed with discretion and directed through the most relevant line within the
            Erenown orbit.
          </p>
        </div>

        {/* Column 4 — Erenown Entities */}
        <div className="col-span-12 md:col-span-12 lg:col-span-4">
          <ColTitle>Erenown Entities</ColTitle>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
            <div data-testid="footer-entity-uk">
              <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-2" style={{ color: T.muted }}>
                United Kingdom
              </div>
              <div className="font-fraunces text-[16px] leading-[1.45]" style={{ color: T.text }}>
                Erenown Ltd
              </div>
              <div className="mt-1 font-intertight text-[12.5px] leading-[1.7]" style={{ color: T.mutedSoft }}>
                Registered in England and Wales<br />
                Company No. 12845592<br />
                Registered Office:<br />
                83 St Marks Road, Enfield,<br />
                EN1 1BJ, United Kingdom
              </div>
            </div>

            <div data-testid="footer-entity-kosovo">
              <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-2" style={{ color: T.muted }}>
                Kosovo
              </div>
              <div className="font-fraunces text-[16px] leading-[1.45]" style={{ color: T.text }}>
                Erenown LLC
              </div>
              <div className="mt-1 font-intertight text-[12.5px] leading-[1.7]" style={{ color: T.mutedSoft }}>
                Ulpianë, C2, H-III, Nr.6<br />
                Bulevardi “Dëshmorët e Kombit”,<br />
                Prishtinë 10000,<br />
                Republic of Kosovo
              </div>
            </div>
          </div>

          <p
            className="mt-6 font-intertight text-[12px] leading-[1.7] max-w-[28rem]"
            style={{ color: T.mutedSoft }}
            data-testid="footer-entities-note"
          >
            Operating through independently established Erenown entities in the United Kingdom and Kosovo.
          </p>
        </div>
      </div>

      {/* Legal links row — titles only for now */}
      <div className="border-t" style={{ borderColor: T.border }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center gap-x-8 gap-y-3 font-plexmono text-[10.5px] tracking-[0.24em] uppercase" style={{ color: T.mutedSoft }}>
          {LEGAL.map((l) => (
            <Link
              key={l.key}
              to={`/legal/${l.key}`}
              data-testid={`footer-legal-${l.key}`}
              className="transition-colors hover:text-white"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom legal note */}
      <div className="border-t" style={{ borderColor: T.border }}>
        <div
          className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 font-plexmono text-[10px] tracking-[0.24em] uppercase"
          style={{ color: T.muted }}
        >
          <div data-testid="footer-legal-note">
            © MMXX · Erenown Ltd · The wider Erenown structure includes independently established Erenown entities in the United Kingdom and Kosovo. Unless otherwise stated, website operation and legal website disclosures are aligned primarily with Erenown Ltd, registered in England and Wales (No. 12845592).
          </div>
        </div>
      </div>
    </footer>
  );
}
